import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, HelpCircle, Shield, CreditCard, Gift, Mail } from 'lucide-react';

/**
 * /faq — Frequently Asked Questions
 *
 * Public, no-auth page. Mirrors what's said elsewhere (T&C, Refund Policy,
 * Privacy) in plain English. Sections are accordion-style for scannability.
 *
 * Last updated: 19 July 2026 (Savings Club overhaul — tier multipliers, per-receipt rewards, minimum-item rule, milestone bonuses, prize draw).
 */

const FAQ_GROUPS = [
  {
    icon: HelpCircle,
    label: 'About PricePAL Compare',
    items: [
      {
        q: 'What is PricePAL Compare?',
        a: (
          <>
            <p>PricePAL Compare is a UK price-comparison platform for groceries, fuel and household
            essentials. We pull live prices from major supermarkets (Tesco, Asda, Sainsbury&apos;s,
            Morrisons, Aldi, Waitrose) and from over 8,000 UK fuel stations (BP, Tesco, Morrisons,
            Sainsbury&apos;s, Asda, JET, Esso, Shell and more) so you can find the cheapest place to
            shop and fuel up — within minutes, by postcode.</p>
          </>
        ),
      },
      {
        q: 'How fresh are the prices?',
        a: (
          <>
            <p><strong>Fuel:</strong> refreshed every 2–24 hours via the official statutory Fuel
            Finder feed.</p>
            <p><strong>Groceries:</strong> refreshed weekly via Apify scrapers, supplemented by
            user-submitted barcode scans and receipt uploads that keep prices sharp for the
            whole community.</p>
            <p>Each price tile shows a freshness badge — <em>fresh</em> (under 24h),
            <em> recent</em> (under 7 days) or <em>stale</em> (older).</p>
          </>
        ),
      },
      {
        q: 'Is PricePAL Compare a retailer?',
        a: <p>No. We never sell, ship, or hold inventory. We aggregate publicly available price
        information from retailers for consumer convenience. When you decide to buy something, you
        complete the purchase directly with the retailer. We may receive an affiliate or referral
        commission from retailers, but <strong>none of that money is ever paid to you in cash</strong>.
        See the Loyalty &amp; Referral Rewards section below for what you do earn.</p>,
      },
    ],
  },
  {
    icon: CreditCard,
    label: 'Subscriptions & Billing',
    items: [
      {
        q: 'What does Premium cost?',
        a: (
          <>
            <p>Premium is <strong>£1.99/month or £19.99/year</strong>. Family Plan is <strong>£2.99/month or £29.99/year</strong> and covers up to 4 family members.</p>
            <p>NHS staff, Blue Light Card holders, students (NUS / TOTUM) and teachers (TRN) qualify for the <strong>£1.00/month Community rate</strong> — verify your ID at <Link to="/community" className="text-violet-700 underline">/community</Link>.</p>
            <p>Eligible new customers start Premium or Family with a <strong>7-day free trial</strong>. A valid payment card is required at sign-up (Stripe authorises the card but does not charge it during the trial). After the 7 days, your card is charged the applicable plan price unless you cancel first. Cancel any time from Settings.</p>
            <p>Prefer not to enter a card? You can still sign up for our <strong>Standard Free plan</strong> — 5 scans/day, 3 price alerts, 2 receipt submissions per month, Basic Fuel Finder, no card required.</p>
          </>
        ),
      },
      {
        q: 'How do I cancel?',
        a: <p>Settings → Billing &amp; Subscription → Manage Subscription. You can also cancel via
        the Stripe billing portal or by emailing <a href="mailto:support@pricepalcompare.com" className="text-violet-700 underline">support@pricepalcompare.com</a>.
        Cancellation takes effect at the end of your current billing period and you keep full
        access until then.</p>,
      },
    ],
  },
  {
    icon: Shield,
    label: 'Refunds & UK Consumer Law',
    items: [
      {
        q: 'Can I get a refund?',
        a: (
          <>
            <p><strong>Yes.</strong> Under UK consumer law you have <strong>two distinct refund
            rights</strong>:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>14-day cooling-off (Consumer Contracts Regulations 2013).</strong> Within
              14 calendar days of your first subscription payment, you are entitled to a full
              refund <em>without giving a reason</em>. PricePAL Compare honours this automatically
              via Settings → Subscription → Request Refund Now.</li>
              <li><strong>Statutory refund rights beyond 14 days (Consumer Rights Act 2015).</strong>
              If the service is not as described, not of satisfactory quality, or not fit for
              purpose — including on renewals — you remain entitled to a refund (in full or
              pro-rata). Submit via the same Settings flow; outside the cooling-off window your
              request is queued for review and you will receive an email outcome within 2
              business days.</li>
            </ul>
            <p className="mt-2">Read the full <Link to="/refund-policy" className="text-violet-700 underline">Refund Policy</Link>.</p>
          </>
        ),
      },
      {
        q: 'Can I get a refund on a renewal?',
        a: <p>Yes — under the Consumer Rights Act 2015. The 14-day cooling-off right under the
        2013 Regulations applies to your <em>initial</em> purchase, but renewals are still
        protected. If a renewal occurred against your intention (you tried to cancel and could not,
        you were charged in error, or the service was significantly degraded), we will review and
        in the majority of reasonable cases refund the renewal in full or pro-rata. Decisions are
        not arbitrary — we provide a written reason if a request cannot be honoured.</p>,
      },
      {
        q: 'How long does a refund take?',
        a: <p>Self-service refunds (within the 14-day window) are approved immediately. The amount
        typically appears back on your card within <strong>5–10 business days</strong>, depending on
        your bank. For post-14-day review requests, expect a written outcome by email within
        <strong> 2 business days</strong>.</p>,
      },
      {
        q: 'What if I disagree with a refund decision?',
        a: (
          <>
            <p>Email <a href="mailto:support@pricepalcompare.com" className="text-violet-700 underline">support@pricepalcompare.com</a> with
            the subject line "formal complaint" — we will issue a written response within 8 weeks.
            If the matter remains unresolved you may escalate to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2 text-sm">
              <li>Citizens Advice consumer helpline — 0808 223 1133 — <a href="https://www.citizensadvice.org.uk" target="_blank" rel="noreferrer" className="text-violet-700 underline">citizensadvice.org.uk</a></li>
              <li>Online Dispute Resolution platform — <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noreferrer" className="text-violet-700 underline">ec.europa.eu/consumers/odr</a></li>
              <li>Small Claims Court (England &amp; Wales) for claims up to £10,000.</li>
            </ul>
            <p className="mt-2 text-sm">Your statutory rights are never excluded by anything in our
            policies — they always prevail over our Terms.</p>
          </>
        ),
      },
    ],
  },
  {
    icon: Gift,
    label: 'Savings Club & Receipt Uploads',
    items: [
      {
        q: 'Is the Savings Club still running?',
        a: (
          <>
            <p><strong>The Savings Club rewards programme is currently paused</strong> while we
            finalise a better rewards catalogue. No new points can be earned and no rewards can
            be redeemed during this pause. Everything else in the app continues to work as
            normal.</p>
            <p className="mt-2">We&apos;ll email everyone the moment the Savings Club is back
            with an improved lineup.</p>
          </>
        ),
      },
      {
        q: 'Should I still upload receipts and scan barcodes while the Savings Club is paused?',
        a: (
          <>
            <p><strong>Yes — please do.</strong> Uploading receipts and scanning barcodes is where
            PricePAL Compare gets most of its value <em>for you personally</em>. It has nothing
            to do with points. Every scan and receipt:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Personalises your <strong>Weekly Shop email</strong> with the exact items you
              buy most often — so it stops feeling generic.</li>
              <li>Sharpens your <strong>price alerts</strong>: we notify you when the products
              already on your receipts drop in price at another supermarket.</li>
              <li>Powers your <strong>Kitchen AI</strong> meal suggestions using ingredients you
              actually keep in the house.</li>
              <li>Shows you which retailer your weekly shop would be cheapest at if you switched
              — real-money savings, not points.</li>
              <li>Improves the community price database so other shoppers see accurate prices
              too.</li>
            </ul>
            <p className="mt-2">Basically: the more you scan and upload, the smarter and more
            personal the app gets for you — regardless of any rewards programme.</p>
          </>
        ),
      },
      {
        q: 'Do points I already earned still count?',
        a: <p>Yes. Your existing points balance is safe and preserved. As soon as the Savings
        Club reopens with the new catalogue, you&apos;ll be able to earn and redeem again with
        the balance you already have.</p>,
      },
      {
        q: 'Do I earn cash from PricePAL Compare?',
        a: <p><strong>No — and this is by design.</strong> PricePAL Compare does not pay cash,
        gift cards, vouchers, cheques, PayPal credit, cryptocurrency or any other monetary
        instrument. When the Savings Club reopens, rewards will be limited to <strong>free
        Premium subscription months</strong> and in-app gamification. The real savings come
        from spotting cheaper prices across supermarkets — which is exactly what the app is
        built to help you do.</p>,
      },
    ],
  },
  {
    icon: Mail,
    label: 'Account, Privacy & Data',
    items: [
      {
        q: 'How do I delete my account?',
        a: <p>Settings → Privacy &amp; Data → Delete Account. The action is irreversible — your
        scan history, saved products and loyalty data will be permanently removed within 30 days
        in line with UK GDPR.</p>,
      },
      {
        q: 'What personal data do you collect?',
        a: <p>Email, name (optional), postcode (optional, for fuel-station distance), barcode
        submissions (anonymised in aggregate price data) and standard analytics events. We never
        sell personal data. Full breakdown in our{' '}
        <Link to="/privacy" className="text-violet-700 underline">Privacy Policy</Link>.</p>,
      },
      {
        q: 'How do I contact support?',
        a: <p><a href="mailto:support@pricepalcompare.com" className="text-violet-700 underline">support@pricepalcompare.com</a>{' '}
        — typical response time 1 business day, max 2.</p>,
      },
    ],
  },
];

const FAQItem = ({ item, idx, groupIdx }) => {
  const [open, setOpen] = useState(false);
  const testId = `faq-item-${groupIdx}-${idx}`;
  return (
    <div
      data-testid={testId}
      className="border border-gray-200 rounded-xl overflow-hidden bg-white hover:border-emerald-300 transition"
    >
      <button
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-start justify-between gap-4 p-4 text-left"
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
      >
        <span className="text-sm md:text-base font-semibold text-gray-900">{item.q}</span>
        <span className="flex-shrink-0 text-gray-400 mt-0.5">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div
          className="px-4 pb-4 -mt-1 text-sm text-gray-700 leading-relaxed border-t border-gray-100 pt-3 space-y-2"
          data-testid={`${testId}-body`}
        >
          {item.a}
        </div>
      )}
    </div>
  );
};

export default function FAQPage() {
  return (
    <>
      <Helmet>
        <title>FAQ — PricePAL Compare</title>
        <meta
          name="description"
          content="Answers to the most common questions about PricePAL Compare: subscriptions, refunds (UK consumer-law compliant), loyalty rewards (free Pro months, no cash payouts), privacy and support."
        />
      </Helmet>

      <main className="min-h-screen bg-gray-50 pb-20" data-testid="faq-page">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-6"
            data-testid="faq-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-2">
              FAQ
            </h1>
            <p className="text-base text-gray-600">
              Plain-English answers — covering refunds (your UK consumer-law rights), how the
              loyalty programme works, what you actually earn, and how to get hold of us.
            </p>
            <p className="text-xs text-gray-400 mt-2">Last updated: 4 June 2026</p>
          </div>

          <div className="space-y-8">
            {FAQ_GROUPS.map((group, gIdx) => {
              const Icon = group.icon;
              return (
                <section key={gIdx} data-testid={`faq-group-${gIdx}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-emerald-700" />
                    </div>
                    <h2 className="text-base md:text-lg font-bold text-gray-900">{group.label}</h2>
                  </div>
                  <div className="space-y-2">
                    {group.items.map((it, iIdx) => (
                      <FAQItem key={iIdx} item={it} idx={iIdx} groupIdx={gIdx} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          <div
            className="mt-10 p-5 rounded-xl bg-emerald-50 border border-emerald-200"
            data-testid="faq-still-stuck"
          >
            <p className="text-sm font-semibold text-emerald-900 mb-1">Still stuck?</p>
            <p className="text-sm text-emerald-900">
              Email{' '}
              <a
                href="mailto:support@pricepalcompare.com"
                className="text-emerald-800 underline"
              >
                support@pricepalcompare.com
              </a>{' '}
              — a real human reads every message, usually within 1 business day.
            </p>
          </div>
        </div>
      </main>
    </>
  );
}
